/**
 * Barcode section of the Pass Designer. An "Enable barcode" toggle gates the
 * whole barcode object: OFF ⇒ emit { barcode: null } so mergePassDesign DELETES
 * the key (null is its explicit delete sentinel; a bare `undefined` would be
 * SKIPPED and leave an existing barcode intact) and the builder omits both
 * `barcodes` and `barcode`. An empty-message barcode object would 422 at mint,
 * so the section must be truly absent when disabled — not zeroed out. When ON:
 * format select, required message, optional altText. The message is [LIVE] when
 * the deliverable is a code — the parent passes `liveMessage` and the field
 * renders read-only with an "Élő adat" badge (the mint refreshes it). The
 * message-required ERROR (barcode.message) surfaces red from the server
 * PassDesignError[].
 */

import type {
  DeepPartial,
  PassDesign,
  PassDesignBarcode,
  PassDesignError,
} from '@insurwiz/wallet-core';

import { BARCODE_FORMAT_OPTIONS } from '../../lib/fieldLabels';

import { HelpText } from './HelpHint';
import { LiveBadge } from './LiveBadge';

export interface BarcodeEditorProps {
  design: PassDesign;
  errors: PassDesignError[];
  onChange: (patch: DeepPartial<PassDesign>) => void;
  /** Present ⇒ message is [LIVE] (read-only, badge-marked); the seeded payload. */
  liveMessage?: string;
}

function notesFor(errors: PassDesignError[], path: string): PassDesignError[] {
  return errors.filter((e) => e.path === path);
}

/** Per-path server findings — red for error (blocks save), amber for warn. Same
 *  severity styling as the sibling ColorsEditor/ImagesEditor sections. */
function ErrorNotes({ errors }: { errors: PassDesignError[] }): JSX.Element | null {
  if (errors.length === 0) return null;
  return (
    <>
      {errors.map((e, i) => (
        <p
          key={`${e.path}-${String(i)}`}
          className={`mt-1 text-xs ${e.severity === 'error' ? 'text-red-600' : 'text-amber-700'}`}
        >
          {e.message}
        </p>
      ))}
    </>
  );
}

export function BarcodeEditor({
  design,
  errors,
  onChange,
  liveMessage,
}: BarcodeEditorProps): JSX.Element {
  const barcode = design.barcode;
  const enabled = barcode !== undefined;
  const messageNotes = notesFor(errors, 'barcode.message');

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-800">Vonalkód / QR</h3>
      <HelpText>
        {design.style === 'boardingPass'
          ? 'A pass alján megjelenő kód. Ha a járat ad kódot, azt a rendszer tölti be.'
          : 'A pass alján megjelenő kód.'}
      </HelpText>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          aria-label="Vonalkód engedélyezése"
          checked={enabled}
          className="accent-brand-700"
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { barcode: { format: 'PKBarcodeFormatQR', message: '' } }
                : { barcode: null },
            )
          }
        />
        <span className="text-sm font-medium text-slate-800">Vonalkód engedélyezése</span>
      </label>

      {enabled ? (
        <>
          <div className="space-y-1">
            <label className="label" htmlFor="design-barcode-format">
              Vonalkód formátum
            </label>
            <select
              id="design-barcode-format"
              aria-label="Vonalkód formátum"
              className="input"
              value={barcode.format}
              onChange={(e) =>
                onChange({ barcode: { format: e.target.value as PassDesignBarcode['format'] } })
              }
            >
              {BARCODE_FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <label className="label" htmlFor="design-barcode-message">
                Vonalkód üzenet
              </label>
              {liveMessage !== undefined ? <LiveBadge /> : null}
            </div>
            <input
              id="design-barcode-message"
              aria-label="Vonalkód üzenet"
              className="input"
              type="text"
              readOnly={liveMessage !== undefined}
              value={liveMessage ?? barcode.message}
              onChange={(e) => onChange({ barcode: { message: e.target.value } })}
            />
            <ErrorNotes errors={messageNotes} />
          </div>

          <div className="space-y-1">
            <label className="label" htmlFor="design-barcode-alt">
              Vonalkód alt szöveg
            </label>
            <input
              id="design-barcode-alt"
              aria-label="Vonalkód alt szöveg"
              className="input"
              type="text"
              value={barcode.altText ?? ''}
              onChange={(e) => onChange({ barcode: { altText: e.target.value } })}
            />
          </div>
        </>
      ) : null}
    </section>
  );
}
